import SimplexNoise from './SimplexNoise.js';

/**
 * CloudLayer — Single parallax cloud layer with per-frame 4D torus noise.
 *
 * UV coordinates are lifted onto a 4D torus, giving inherently seamless tiling.
 * Time is embedded as a circular offset in 4D, creating smooth continuous evolution.
 * The tile is regenerated fresh every frame at low resolution — no caching,
 * no crossfade, no popping. Bilinear upscale makes low-res look natural for clouds.
 *
 * The tile is generated at (size+2)x(size+2) with a 1px wrapped border so that
 * bilinear interpolation at tile edges has correct neighbor data (no seams).
 *
 * Each layer has a tileScale that controls how large the tile renders on screen.
 * Different tileScales per layer break alignment so layers complement rather than stack.
 *
 * Technique from looptile: 4D torus mapping for seamless spatial + temporal noise.
 */

const DEFAULT_TILE_SIZE = 64;
const TAU = Math.PI * 2;

export default class CloudLayer {
  constructor(options, seed = 0, tileSize = DEFAULT_TILE_SIZE) {
    this.speed = options.speed;
    this.noiseScale = options.noiseScale;
    this.octaves = Math.min(options.octaves, 3); // cap at 3 for per-frame budget
    this.opacity = options.opacity;
    this.parallaxFactor = options.parallaxFactor;
    this.coverageThreshold = options.coverageThreshold;
    this.edgeSoftness = options.edgeSoftness;
    this.evolutionSpeed = options.evolutionSpeed;
    this.colorBase = options.colorBase;
    this.colorShadow = options.colorShadow;
    this.tileScale = options.tileScale || 3;
    this._tileSize = tileSize;

    this._noise = new SimplexNoise(seed);
    this._time = 0;

    // Tile canvas is (size+2)x(size+2): core size pixels + 1px wrapped border on each side.
    // This gives bilinear interpolation correct neighbor data at tile edges.
    const padded = tileSize + 2;
    this._tile = document.createElement('canvas');
    this._tile.width = padded;
    this._tile.height = padded;
    this._tileCtx = this._tile.getContext('2d');
    this._imageData = this._tileCtx.createImageData(padded, padded);

    // Precompute spatial trig for torus mapping.
    // cos/sin(i/tileSize * TAU) — same table reused for both U and V axes.
    this._cosSpatial = new Float64Array(tileSize);
    this._sinSpatial = new Float64Array(tileSize);
    for (let i = 0; i < tileSize; i++) {
      const angle = (i / tileSize) * TAU;
      this._cosSpatial[i] = Math.cos(angle);
      this._sinSpatial[i] = Math.sin(angle);
    }

    // Precompute per-octave phase trig (angle-addition constants).
    // U-axis phase = octave * 2.3, V-axis phase = octave * 2.3 * 1.7
    const oct = this.octaves;
    this._cosPhaseU = new Float64Array(oct);
    this._sinPhaseU = new Float64Array(oct);
    this._cosPhaseV = new Float64Array(oct);
    this._sinPhaseV = new Float64Array(oct);
    for (let i = 0; i < oct; i++) {
      const phase = i * 2.3;
      this._cosPhaseU[i] = Math.cos(phase);
      this._sinPhaseU[i] = Math.sin(phase);
      this._cosPhaseV[i] = Math.cos(phase * 1.7);
      this._sinPhaseV[i] = Math.sin(phase * 1.7);
    }

    // Precompute per-octave FBM constants
    this._octRadii = new Float64Array(oct);
    this._octAmps = new Float64Array(oct);
    this._invMaxAmp = 0;
    const baseRadius = 1.0 / (this.noiseScale * 768);
    let maxAmp = 0, amp = 1, freq = 1;
    for (let i = 0; i < oct; i++) {
      this._octRadii[i] = baseRadius * freq;
      this._octAmps[i] = amp;
      maxAmp += amp;
      amp *= 0.5;   // persistence
      freq *= 2.0;  // lacunarity
    }
    this._invMaxAmp = 1 / maxAmp;
    this._temporalRadius = baseRadius * 0.4 * this.evolutionSpeed * 10;

    // Unwrapped camera angle tracking
    this._lastAngle = null;
    this._unwrappedAngle = 0;

    // Generate initial frame
    this._generateTile(0);
  }

  // ── Per-frame tile generation ──

  _generateTile(time) {
    const data = this._imageData.data;
    const noise = this._noise;
    const size = this._tileSize;
    const padded = size + 2;
    const octaves = this.octaves;
    const threshold = this.coverageThreshold;
    const softness = this.edgeSoftness;
    const [br, bg, bb] = this.colorBase;
    const [sr, sg, sb] = this.colorShadow;

    // Temporal trig — only 2 trig calls per tile regeneration
    const at = time * TAU;
    const tempX = Math.cos(at) * this._temporalRadius;
    const tempW = Math.sin(at) * this._temporalRadius;

    // Precomputed lookup tables
    const cosSpatial = this._cosSpatial;
    const sinSpatial = this._sinSpatial;
    const cosPhaseU = this._cosPhaseU;
    const sinPhaseU = this._sinPhaseU;
    const cosPhaseV = this._cosPhaseV;
    const sinPhaseV = this._sinPhaseV;
    const octRadii = this._octRadii;
    const octAmps = this._octAmps;
    const invMaxAmp = this._invMaxAmp;

    // Loop from -1 to size (inclusive) to generate core + 1px wrapped border.
    // Border pixels use modular index so they match the opposite edge.
    for (let py = -1; py <= size; py++) {
      const vi = (((py % size) + size) % size);
      const cosVBase = cosSpatial[vi];
      const sinVBase = sinSpatial[vi];

      for (let px = -1; px <= size; px++) {
        const ui = (((px % size) + size) % size);
        const cosUBase = cosSpatial[ui];
        const sinUBase = sinSpatial[ui];

        // Inline torus-mapped FBM with precomputed angle-addition
        let value = 0;
        for (let o = 0; o < octaves; o++) {
          const r = octRadii[o];

          // angle-addition: cos(u*TAU + phase) = cosU*cosP - sinU*sinP
          const cosAx = cosUBase * cosPhaseU[o] - sinUBase * sinPhaseU[o];
          const sinAx = sinUBase * cosPhaseU[o] + cosUBase * sinPhaseU[o];
          const cosAy = cosVBase * cosPhaseV[o] - sinVBase * sinPhaseV[o];
          const sinAy = sinVBase * cosPhaseV[o] + cosVBase * sinPhaseV[o];

          value += octAmps[o] * noise.noise4D(
            cosAx * r,
            sinAx * r + tempX,
            cosAy * r,
            sinAy * r + tempW
          );
        }
        const n = value * invMaxAmp;

        // Remap [-1,1] to [0,1]
        const raw = (n + 1) * 0.5;

        // Coverage with soft edge
        let coverage = 0;
        if (raw > threshold) {
          coverage = Math.min(1, (raw - threshold) / softness);
        }

        const shadeFactor = raw;
        const cr = sr + (br - sr) * shadeFactor;
        const cg = sg + (bg - sg) * shadeFactor;
        const cb = sb + (bb - sb) * shadeFactor;

        const idx = ((py + 1) * padded + (px + 1)) * 4;
        data[idx] = cr;
        data[idx + 1] = cg;
        data[idx + 2] = cb;
        data[idx + 3] = coverage * 255;
      }
    }

    this._tileCtx.putImageData(this._imageData, 0, 0);
  }

  // ── Update ──

  /** Accumulate time every frame for smooth drift/parallax. */
  advanceTime(deltaTime) {
    this._time += deltaTime;
  }

  /** Regenerate noise tile. Called once every N frames (round-robin). */
  regenerateTile() {
    this._generateTile(this._time * this.evolutionSpeed);
  }

  // ── Render ──

  render(ctx, width, height, camera) {
    const time = this._time;

    const driftX = time * this.speed.x;
    const driftY = time * this.speed.y;

    // Track unwrapped angle
    const angle = camera.angle;
    if (this._lastAngle !== null) {
      let delta = angle - this._lastAngle;
      if (delta > 180) delta -= 360;
      if (delta < -180) delta += 360;
      this._unwrappedAngle += delta;
    }
    this._lastAngle = angle;

    const rotationPx = this._unwrappedAngle * -12.0 * this.parallaxFactor;
    const skewPx = camera.skewAngle * 250.0 * this.parallaxFactor;

    const zoom = camera.zoomLevel || 1;
    const posScale = this.parallaxFactor * 0.01 / zoom;
    const posPxX = camera.position.x * posScale;
    const posPxZ = camera.position.z * posScale;

    const totalOffsetX = driftX + rotationPx + posPxX;
    const totalOffsetY = driftY + skewPx + posPxZ;

    this._drawTiled(ctx, width, height, totalOffsetX, totalOffsetY);
  }

  _drawTiled(ctx, width, height, offsetX, offsetY) {
    ctx.globalAlpha = this.opacity;

    // Visual size stays constant regardless of tile resolution (64 is the reference)
    const viewportFactor = Math.max(width, height) / 540;
    const scaledSize = DEFAULT_TILE_SIZE * this.tileScale * viewportFactor;

    const wrappedX = ((offsetX % scaledSize) + scaledSize) % scaledSize;
    const wrappedY = ((offsetY % scaledSize) + scaledSize) % scaledSize;
    const fracX = wrappedX - Math.floor(wrappedX);
    const fracY = wrappedY - Math.floor(wrappedY);
    const intX = -Math.floor(wrappedX);
    const intY = -Math.floor(wrappedY);

    ctx.save();
    ctx.translate(-fracX, -fracY);

    // Draw only the inner size x size portion of the (size+2)x(size+2) tile.
    // The 1px wrapped border exists so bilinear interpolation at edges samples
    // correct neighbor data, but we don't render the border pixels themselves.
    const sz = this._tileSize;
    for (let y = intY; y < height + scaledSize; y += scaledSize) {
      for (let x = intX; x < width + scaledSize; x += scaledSize) {
        ctx.drawImage(this._tile, 1, 1, sz, sz, x, y, scaledSize, scaledSize);
      }
    }

    ctx.restore();
    ctx.globalAlpha = 1;
  }
}
